import React, { useState, useCallback } from 'react';

export type HandledError = {
    code: string;
    message: string;
    details?: unknown;
};

type APIErrorContextType = {
    error: HandledError | null;
    addError: (message: string, details?: unknown) => Error;
    removeError: () => void;
};

export const APIErrorContext = React.createContext<APIErrorContextType>({
    error: null,
    addError: () => new Error(),
    removeError: () => {}
});

const normalizeErrorMessage = (message: unknown): string => {
    const raw = String(message ?? '1000')
        .trim()
        .replace(/^"|"$/g, '');
    return raw || '1000';
};

type APIErrorProviderProps = { children: React.ReactNode };

export default function APIErrorProvider({ children }: APIErrorProviderProps) {
    const [error, setError] = useState<HandledError | null>(null);

    const removeError = useCallback(() => setError(null), []);

    const addError = useCallback((message: string, details?: unknown) => {
        const normalizedMessage = normalizeErrorMessage(message);
        const handled: HandledError = {
            code: normalizedMessage,
            message: normalizedMessage,
            details
        };
        setError(handled);
        console.error('error', handled);
        // Conservar details en el Error lanzado para que un catch que haga
        // addError(err.message, err.details) no pierda la interpolación i18n.
        const normalizedError = new Error(normalizedMessage) as Error & { details?: unknown };
        normalizedError.details = details;
        return normalizedError;
    }, []);

    const contextValue: APIErrorContextType = {
        error,
        addError,
        removeError
    };

    return <APIErrorContext.Provider value={contextValue}>{children}</APIErrorContext.Provider>;
}
